import { Tab } from '@headlessui/react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabUserData } from '../tabs/TabUserData';
import { TabPaymentHistory } from '../tabs/TabPaymentHistory';
import { useSelector } from 'react-redux';
import { TabCoursesHistory } from '../tabs/TabCoursesHistory';
import { WalletIcon } from '@heroicons/react/24/outline';
import { TabActivity } from '../tabs/TabActivity';
import { TabStudentNotes } from '../tabs/TabStudentNotes';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export const TabsProfileUser = ({
    isOpen,
    onEditModeChange,
    onSaveCallbackChange,
    onCancelCallbackChange,
    onLoadingSavingChange,
    onUserUpdated,
    userId,
    refreshTable,
    onStudentNotesFormActiveChange,
    onStudentNotesSaveActionChange,
    onStudentNotesCancelActionChange,
    onStudentNotesFooterSaveLabelChange,
    onStudentNotesFooterLoadingChange,
    onClearStudentNotesFooter,
}) => {
    const { userSelected } = useSelector((state) => state.users);
    const { user } = useSelector((state) => state.user);
    const { typeUser: typeUserSelected } = userSelected;
    const { typeUser: typeUserLogged } = user;
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [isEditingUserData, setIsEditingUserData] = useState(false);
    const cancelEditRef = useRef(null);
    const cancelStudentNotesRef = useRef(null);

    // Enable view tabs by type user selected
    const enableViewUserProfile = ['estudiante', 'profesor', 'control escolar', 'administrativo', 'desarrollador', 'director', 'subdirector', 'coordinador', 'ventas', 'cobranza', 'invitado'];
    const enableViewPaymentHistory = ['estudiante'];
    const enableViewHistoryCourses = ['estudiante', 'profesor'];
    const enableViewActivityHistory = ['control escolar', 'administrativo', 'desarrollador', 'director', 'subdirector', 'coordinador', 'ventas', 'cobranza', 'invitado'];
    const enableViewStudentNotes = typeUserLogged !== 'estudiante' && typeUserSelected === 'estudiante';

    const tabsList = [
        {
            label: 'datos',
            allowView: enableViewUserProfile.includes(typeUserSelected),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            label: 'pagos',
            allowView: enableViewPaymentHistory.includes(typeUserSelected),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
            )
        },
        {
            label: 'cursos',
            allowView: enableViewHistoryCourses.includes(typeUserSelected),
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            )
        },
        {
            label: 'notas',
            allowView: enableViewStudentNotes,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
            )
        },
        {
            label: 'actividades',
            allowView: enableViewActivityHistory.includes(typeUserLogged),
            icon: <WalletIcon className="w-5 h-5" />
        },
    ];

    // Filter tabs based on allowView
    const visibleTabs = tabsList.filter(tab => tab.allowView);
    const userDataTabIndex = visibleTabs.findIndex((tab) => tab.label === 'datos');
    const notesTabIndex = visibleTabs.findIndex((tab) => tab.label === 'notas');

    /** Notas con estatus abierta o en seguimiento (misma lógica que el listado de estudiantes). */
    const studentNotesOpenOrFollowUpCount = useMemo(() => {
        const notes = userSelected?.studentNotes;
        if (!Array.isArray(notes) || notes.length === 0) return 0;
        return notes.filter(
            (n) => n?.status === 'abierta' || n?.status === 'en seguimiento'
        ).length;
    }, [userSelected?.studentNotes]);

    // Reset tab index cuando cambia el usuario o se abre el drawer
    useEffect(() => {
        setSelectedTabIndex(0);
    }, [userId, isOpen]);

    // Validar que el índice seleccionado sea válido
    useEffect(() => {
        if (selectedTabIndex >= visibleTabs.length) {
            setSelectedTabIndex(0);
        }
    }, [visibleTabs.length, selectedTabIndex]);

    const handleEditModeChange = useCallback(
        (isEditing) => {
            setIsEditingUserData(isEditing);
            onEditModeChange?.(isEditing);
        },
        [onEditModeChange],
    );

    const handleCancelCallbackChange = useCallback((callback) => {
        cancelEditRef.current = () => {
            if (!callback) return;
            const maybeHandler = callback();
            if (typeof maybeHandler === 'function') {
                maybeHandler();
            }
        };
        onCancelCallbackChange?.(callback);
    }, [onCancelCallbackChange]);

    const registerStudentNotesTabAwayCancel = useCallback((fn) => {
        cancelStudentNotesRef.current = fn;
    }, []);

    const handleTabChange = (nextTabIndex) => {
        // If leaving "datos" while editing, cancel edit mode first.
        if (
            userDataTabIndex !== -1 &&
            selectedTabIndex === userDataTabIndex &&
            nextTabIndex !== userDataTabIndex &&
            isEditingUserData
        ) {
            cancelEditRef.current && cancelEditRef.current();
            // Keep drawer footer in sync immediately on tab switch.
            setIsEditingUserData(false);
            if (onEditModeChange) onEditModeChange(false);
        }
        // Si sale de "notas", cancelar alta/edición de notas y limpiar footer del drawer.
        if (
            notesTabIndex !== -1 &&
            selectedTabIndex === notesTabIndex &&
            nextTabIndex !== notesTabIndex
        ) {
            cancelStudentNotesRef.current && cancelStudentNotesRef.current();
            if (onClearStudentNotesFooter) onClearStudentNotesFooter();
        }
        setSelectedTabIndex(nextTabIndex);
    };

    return (
        <div className="w-full h-full flex flex-col">
            <Tab.Group selectedIndex={selectedTabIndex} onChange={handleTabChange}>
                {() => (
                    <div className="h-full flex flex-col">
                        {/* Scroll horizontal en móvil, barra de scroll oculta */}
                        <div className="flex-shrink-0 border-b border-gray-200 bg-white overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <Tab.List className="flex border-b-0 min-w-max md:min-w-0 md:w-full">
                                {visibleTabs.map((tab) => {
                                    const { label, icon } = tab;
                                    return (
                                        <Tab
                                            key={label}
                                            className={({ selected }) =>
                                                classNames(
                                                    'group relative flex items-center justify-center gap-2 flex-shrink-0 md:flex-1 px-4 py-3.5 text-sm font-semibold transition-all duration-200 focus:outline-none',
                                                    selected
                                                        ? 'text-indigo-600'
                                                        : 'text-gray-600 hover:text-indigo-500 hover:bg-gray-50'
                                                )
                                            }
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span className={classNames(
                                                        'transition-all duration-200',
                                                        selected ? 'scale-110' : 'group-hover:scale-105'
                                                    )}>
                                                        {icon}
                                                    </span>
                                                    {label === 'notas' && studentNotesOpenOrFollowUpCount > 0 ? (
                                                        <span className="inline-flex items-baseline gap-1 capitalize tracking-wide whitespace-nowrap">
                                                            <span>{label}</span>
                                                            <span
                                                                className="relative inline-flex h-5 min-w-[1.25rem] shrink-0 -translate-y-[9px] items-center justify-center"
                                                            >
                                                                {/* Halo: animación dedicada (sombra + opacidad; pulse genérico casi invisible aquí) */}
                                                                <span
                                                                    className="pointer-events-none absolute -inset-[4px] rounded-full border-2 border-rose-400 animate-notesBadgeRing"
                                                                    aria-hidden="true"
                                                                />
                                                                <span
                                                                    className="relative z-[1] inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-700 px-1.5 text-[11px] font-semibold text-white tabular-nums shadow-sm ring-1 ring-white/30"
                                                                >
                                                                    {studentNotesOpenOrFollowUpCount > 99 ? '99+' : studentNotesOpenOrFollowUpCount}
                                                                </span>
                                                            </span>
                                                        </span>
                                                    ) : (
                                                        <span className="capitalize tracking-wide whitespace-nowrap">
                                                            {label}
                                                        </span>
                                                    )}
                                                    {/* Indicador inferior animado */}
                                                    {selected && (
                                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/50" />
                                                    )}
                                                </>
                                            )}
                                        </Tab>
                                    )
                                }
                                )}
                            </Tab.List>
                        </div>
                        <Tab.Panels className="bg-white flex-1 overflow-auto">
                            {visibleTabs.map((tab, index) => {
                                const { label } = tab;
                                return (
                                    <Tab.Panel
                                        key={label}
                                        className={classNames(
                                            'p-6 focus:outline-none h-full',
                                            'transition-all duration-300 ease-in-out'
                                        )}
                                    >
                                        {label === 'datos' && (
                                            <TabUserData 
                                                isOpen={isOpen}
                                                onEditModeChange={handleEditModeChange}
                                                onSaveCallbackChange={onSaveCallbackChange}
                                                onCancelCallbackChange={handleCancelCallbackChange}
                                                onLoadingSavingChange={onLoadingSavingChange}
                                                onUserUpdated={onUserUpdated}
                                                userId={userId}
                                                refreshTable={refreshTable}
                                            />
                                        )}
                                        {label === 'pagos' && <TabPaymentHistory />}
                                        {label === 'cursos' && <TabCoursesHistory />}
                                        {label === 'actividades' && <TabActivity />}
                                        {label === 'notas' && (
                                            <TabStudentNotes
                                                userId={userId}
                                                isOpen={isOpen}
                                                onStudentNotesFormActiveChange={onStudentNotesFormActiveChange}
                                                onStudentNotesSaveActionChange={onStudentNotesSaveActionChange}
                                                onStudentNotesCancelActionChange={onStudentNotesCancelActionChange}
                                                onStudentNotesFooterSaveLabelChange={onStudentNotesFooterSaveLabelChange}
                                                onStudentNotesFooterLoadingChange={onStudentNotesFooterLoadingChange}
                                                onRegisterStudentNotesTabAwayCancel={registerStudentNotesTabAwayCancel}
                                                onClearStudentNotesFooter={onClearStudentNotesFooter}
                                                onUserUpdated={onUserUpdated}
                                            />
                                        )}
                                    </Tab.Panel>
                                )
                            })}
                        </Tab.Panels>
                    </div>
                )}
            </Tab.Group>
        </div>
    )
}